import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CONSECUTIVE_MS = 400; // max gap between clicks to count as "consecutive"

const ScrollNav = ({ sections = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const ticking = useRef(false);
  const showTimer = useRef(null);
  const lastUp = useRef(0);
  const lastDown = useRef(0);

  // --- Track current section ---
  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY + window.innerHeight * 0.35;
      let best = 0;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollY) {
          best = i;
          break;
        }
      }
      setActiveIndex(best);
      ticking.current = false;
    });
  }, [sections]);

  // --- Show/hide ---
  useEffect(() => {
    const onScroll = () => {
      handleScroll();
      setVisible(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [handleScroll]);

  useEffect(() => {
    const reset = () => {
      setVisible(true);
      clearTimeout(showTimer.current);
      showTimer.current = setTimeout(() => setVisible(false), 3500);
    };
    window.addEventListener("scroll", reset, { passive: true });
    window.addEventListener("mousemove", reset, { passive: true });
    return () => {
      window.removeEventListener("scroll", reset);
      window.removeEventListener("mousemove", reset);
      clearTimeout(showTimer.current);
    };
  }, []);

  // --- Navigation ---
  const scrollToSection = useCallback(
    (index) => {
      const clamped = Math.max(0, Math.min(index, sections.length - 1));
      const el = document.getElementById(sections[clamped].id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      setActiveIndex(clamped);
    },
    [sections]
  );

  // Cancel any in-progress smooth scroll then start a new one
  const forceScrollTo = useCallback((targetY) => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    // stop current smooth scroll by setting auto and scrolling to current position
    html.style.scrollBehavior = "auto";
    window.scrollTo({ top: window.scrollY });
    // force reflow so the stop is applied before starting new smooth scroll
    html.offsetHeight;
    html.style.scrollBehavior = "smooth";
    window.scrollTo({ top: targetY, behavior: "smooth" });
    // restore previous value (usually empty)
    html.style.scrollBehavior = prev;
  }, []);

  const handleUp = () => {
    const now = Date.now();
    const gap = now - lastUp.current;
    lastUp.current = now;

    if (gap < CONSECUTIVE_MS) {
      // Double-tap → top
      forceScrollTo(0);
      setActiveIndex(0);
    } else {
      scrollToSection(activeIndex - 1);
    }
  };

  const handleDown = () => {
    const now = Date.now();
    const gap = now - lastDown.current;
    lastDown.current = now;

    if (gap < CONSECUTIVE_MS) {
      // Double-tap → bottom
      forceScrollTo(document.body.scrollHeight);
      setActiveIndex(sections.length - 1);
    } else {
      scrollToSection(activeIndex + 1);
    }
  };

  if (!sections.length) return null;

  const isFirst = activeIndex === 0;
  const isLast = activeIndex === sections.length - 1;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed right-5 bottom-6 z-50 select-none"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <div
            className="flex flex-col items-center rounded-full py-1 px-1"
            style={{
              background: "var(--nav-bg)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid var(--border)",
              boxShadow:
                "0 2px 16px rgba(0,0,0,0.07), 0 0 0 0.5px rgba(255,255,255,0.12) inset",
            }}
          >
            <button
              onClick={handleUp}
              aria-disabled={isFirst}
              aria-label="Yukarı"
              className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-150 hover:bg-white/5"
              style={{
                opacity: isFirst ? 0.25 : 1,
                color: "var(--fg)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>

            <button
              onClick={handleDown}
              aria-disabled={isLast}
              aria-label="Aşağı"
              className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-150 hover:bg-white/5"
              style={{
                opacity: isLast ? 0.25 : 1,
                color: "var(--fg)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollNav;
